<script lang="ts">

import {defineComponent, ref, onMounted, type PropType} from 'vue';
import type {TranslationList} from "~/types/Utils";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import TravelOfferCoverageCard from "~/components/travel/TravelOfferCoverageCard.vue";
import type {ApiOffer} from "~/types/Travel";

export default defineComponent(
    {
      name: 'TravelOffer',
      components: {TravelOfferCoverageCard},
      props: {
        offer: {
          type: Object as PropType<any>, // Adjust the type as per your data structure
          required: true,
        },
      },
      setup(props, {emit}) {
        const translationList = ref<TranslationList>({});
        const translationStore = useTranslationStore();

        onMounted(async () => {
          translationList.value = await translationStore.getTranslations('ro', 'dashboard');
        });

        const tooltip = ref(true);

        const isPackageNameLong = computed(() => {
          return props.offer.product_name.length > 20;
        });

        const onSelectedOffer = (selectedOffer: ApiOffer) => {
          emit('selectedOffer', selectedOffer);
        };

        return {
          translationList,
          translation,
          tooltip,
          isPackageNameLong,
          onSelectedOffer
        };
      },
    }
);

</script>

<template>
  <div>
    <v-container class="container">
      <v-row class="d-flex align-center justify-center border-expansion my-2 mx-sm-0 mx-md-0 mx-lg-0 mx-2  px-0">
        <v-col cols="12" sm="6" md="2">
          <v-card color="ghost-white" rounded="lg" flat class="d-flex align-center justify-center pa-1">
            <NuxtImg :src="'/images/companies/insurance/thumbnails/' + offer.travel_insurance_company.code + '.webp'"
                     :alt="offer.travel_insurance_company.name" class="pa-1 w-100 pulse animated"/>
          </v-card>
        </v-col>
        <v-col class="d-flex flex-wrap justify-center px-0" cols="12" sm="12" md="8">
          <travel-offer-coverage-card
              icon="mdi-shield-check"
              text-body="Accidente și boală"
              :is-included="!!offer.is_base_coverage_included"
              :max-coverage="offer.base_coverage_amount_insured ? offer.base_coverage_amount_insured : null"
          >
          </travel-offer-coverage-card>
          <travel-offer-coverage-card
              icon="mdi-bag-suitcase"
              text-body="Asigurare bagaje"
              :is-included="!!offer.is_baggage_loss_coverage_included"
              :max-coverage="offer.baggage_loss_coverage_amount_insured ? offer.baggage_loss_coverage_amount_insured : null"
          >
          </travel-offer-coverage-card>
          <travel-offer-coverage-card
              icon="mdi-ski"
              text-body="Activități sportive"
              :is-included="!!offer.is_sports_coverage_included"
              :max-coverage="offer.sports_coverage_amount_insured ? offer.sports_coverage_amount_insured : null"
          >
          </travel-offer-coverage-card>
          <travel-offer-coverage-card
              icon="mdi-airplane"
              text-body="Întarzirea avionului"
              :is-included="!!offer.is_flight_delay_coverage_included"
              :max-coverage="offer.flight_delay_coverage_amount_insured ? offer.flight_delay_coverage_amount_insured : null"
          >
          </travel-offer-coverage-card>
          <travel-offer-coverage-card
              icon="mdi-shield-account"
              text-body="Răspundere civilă"
              :is-included="!!offer.is_travel_liability_coverage_included"
              :max-coverage="offer.travel_liability_coverage_amount_insured ? offer.travel_liability_coverage_amount_insured : null"
          >
          </travel-offer-coverage-card>
          <travel-offer-coverage-card
              icon="mdi-hand-coin"
              text-body="Storno"
              :is-included="!!offer.is_storno_costs_coverage_included"
              :max-coverage="offer.storno_costs_coverage_amount_insured ? offer.storno_costs_coverage_amount_insured : null"
          >
          </travel-offer-coverage-card>
          <travel-offer-coverage-card
              icon="mdi-car-wrench"
              text-body="Asistență rutieră"
              :is-included="!!offer.is_road_assistance_coverage_included"
              :max-coverage="offer.road_assistance_coverage_amount_insured ? offer.road_assistance_coverage_amount_insured : null"
          >
          </travel-offer-coverage-card>
        </v-col>
        <v-col cols="12" sm="6" md="2" class="d-flex justify-center align-center">
          <v-hover>
            <template v-slot:default="{ isHovering, props }">
              <v-card
                  v-bind="props"
                  color="primary"
                  elevation="0"
                  link
                  rounded="lg"
                  class="pa-3 fw-700 fs-22"
                  @click="onSelectedOffer(offer)"
              >
                <v-row class="d-flex align-center justify-center">
                  <v-col cols="12" sm="12">
                    <div class="fw-700 fs-22">{{ offer.amount }} {{ offer.currency }}</div>
                    <div class="text-center">
                      <v-icon color="white">mdi-cart</v-icon>
                    </div>
                  </v-col>
                  <v-tooltip
                      activator="parent"
                      location="top"
                  >
                    <span>{{ translation(translationList.add_to_cart, 'Adaugă în coș') }}</span>
                  </v-tooltip>
                </v-row>

                <v-tooltip v-if="isPackageNameLong"
                           activator="parent"
                           location="bottom"
                           content-class="tooltip-info text-white"
                >
                  <div class="pa-2 fw-700 fs-16">
                    {{ offer.product_name }}
                  </div>
                </v-tooltip>
              </v-card>
            </template>
          </v-hover>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<style scoped>

</style>
